import { useTranslation } from "react-i18next";
import CircularCountdown from "../circularCountdown/circularCountdown";
import { WorktimeSchedule } from "../../models/worktimeSchedule";
import { WorkScheduleModel } from "../../models/worktimeResult";

interface TechSupportStatusProps {
  worktimes: WorktimeSchedule | null;
}

const TechSupportStatus = ({ worktimes }: TechSupportStatusProps) => {
   const { t } = useTranslation();

  if (worktimes == null) {
    return (
      <div className="space-y-2 p-4">
        <div className="flex items-center">
          <div className="h-5 w-24 bg-gray-200 rounded animate-pulse mr-3"></div>
          <div className="h-8 w-16 bg-gray-200 rounded-md animate-pulse"></div>
        </div>
        <div className="h-10 w-full bg-gray-200 rounded-tr-2xl rounded-bl-2xl animate-pulse"></div>
      </div>
    );
  }

  const now = new Date();
  const currentDay = now.getDay(); // 0 - воскресенье, 1 - понедельник и т.д.
  const currentTime = now.getTime();

  // Находим расписание для текущего дня
  const todaySchedule = worktimes.schedules.find(schedule => 
    schedule.day === currentDay
  );

  // Проверяем, есть ли расписание на сегодня и не является ли день выходным
  const isDayOff = !todaySchedule || todaySchedule.start === todaySchedule.end;

  if (isDayOff) {
    return (
      <div className="space-y-2 p-2">
        <div className="flex items-center">
          <p className="pr-3">{t("Techsupport")}</p>
          <div className="flex items-center py-2 px-3 rounded-md bg-red-800">
            <span className="text-red-400">{t("Close")} ({t("Day off")})</span>
          </div>
        </div>
      </div>
    );
  }

  // Парсим время начала и окончания работы из расписания
  const [startHour, startMinute] = todaySchedule.start.split(':').map(Number);
  let [endHour, endMinute] = todaySchedule.end.split(':').map(Number);

  // Создаем Date объекты для времени начала и окончания работы сегодня
  const workStartToday = new Date(now);
  workStartToday.setHours(startHour, startMinute, 0, 0);
  
  const workEndToday = new Date(now);
  if (endHour < startHour)
    endHour += 24;
  workEndToday.setHours(endHour, endMinute, 0, 0);

  // Проверяем, находимся ли мы в общем рабочем времени сегодня
  const isWithinWorkingHours = currentTime >= workStartToday.getTime() && 
                              currentTime <= workEndToday.getTime();

  // Проверяем, не находимся ли мы в перерыве (open/close)
  const isDuringBreak = worktimes.open && worktimes.close && 
                       currentTime <= worktimes.open.getTime() && 
                       currentTime >= worktimes.close.getTime();

  // Техподдержка закрыта, если:
  // 1. Сейчас не рабочее время по расписанию ИЛИ
  // 2. Сейчас перерыв (если он указан)
  const isClosed = !isWithinWorkingHours || isDuringBreak;

  // Определяем время для Countdown:
  // - Если до открытия, то показываем время до workStartToday
  // - Если перерыв, то показываем время до worktimes.close
  // - Если рабочий день закончился, показываем время до следующего рабочего дня
  let countdownTime = 0;
  if (!isWithinWorkingHours && currentTime < workStartToday.getTime()) {
    countdownTime = workStartToday.getTime();
  } else if (isDuringBreak) {
    countdownTime = worktimes.open.getTime();
  } else if (!isWithinWorkingHours && currentTime > workEndToday.getTime()) {
    // Находим следующий рабочий день
    let nextWorkDay = currentDay;
    let nextWorkSchedule: WorkScheduleModel | undefined;
    
    for (let i = 1; i <= 7; i++) {
      nextWorkDay = (currentDay + i) % 7;
      nextWorkSchedule = worktimes.schedules.find(s => s.day === nextWorkDay && s.start !== s.end);
      if (nextWorkSchedule) break;
    }

    if (nextWorkSchedule) {
      const nextWorkDate = new Date(now);
      // Переход на следующий день (или несколько дней)
      const daysToAdd = (nextWorkDay - currentDay + 7) % 7 || 7;
      nextWorkDate.setDate(nextWorkDate.getDate() + daysToAdd);
      
      const [nextStartHour, nextStartMinute] = nextWorkSchedule.start.split(':').map(Number);
      nextWorkDate.setHours(nextStartHour, nextStartMinute, 0, 0);
      
      countdownTime = nextWorkDate.getTime();
    }
  }

  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center">
        <p className="pr-3">{t("Techsupport")}</p>
        {isClosed ? (
          <div className="flex items-center py-2 px-3 rounded-md bg-red-800">
            <span className="text-red-400">{t("Close")}</span>
          </div>
        ) : (
          <div className="flex items-center py-2 px-3 rounded-md bg-green-800">
            <span className="text-green-400">{t("Open")}</span>
          </div>
        )}
      </div>

      {isClosed && countdownTime > 0 && (
        <div className="w-full bg-secondary rounded-tr-2xl rounded-bl-2xl">
          <CircularCountdown
            targetTime={countdownTime}
          />
        </div>
      )}
    </div>
  );
};

export default TechSupportStatus;